import { z } from 'zod'

const unwrapToInner = (schema: z.ZodType): z.ZodType => {
  if (schema instanceof z.ZodOptional || schema instanceof z.ZodNullable) {
    return unwrapToInner(schema.unwrap() as z.ZodType)
  }
  if (schema instanceof z.ZodDefault) {
    return unwrapToInner(schema.unwrap() as z.ZodType)
  }
  return schema
}

const withMetadata = <T extends z.ZodType>(target: T, ...sources: z.ZodType[]): T => {
  const metadata: Record<string, unknown> = {}
  for (const source of sources) {
    const m = source.meta()
    if (m) Object.assign(metadata, m)
  }
  return Object.keys(metadata).length > 0 ? target.meta(metadata) : target
}

const ignoreChecks = (schema: z.ZodType): z.ZodType => {
  const def = schema.def
  const newDef = { ...def, checks: [] }
  if ('element' in newDef && newDef['element'] != null && '_zod' in (newDef['element'] as object)) {
    newDef['element'] = ignoreChecks(newDef['element'] as z.ZodType)
  }
  return schema.clone(newDef as typeof schema.def)
}

export function deepPartial<T extends z.ZodObject>(schema: T): z.ZodType {
  const shape = schema.shape as Record<string, z.ZodType>
  const newShape: Record<string, z.ZodType> = {}

  for (const [key, value] of Object.entries(shape)) {
    const inner = unwrapToInner(value)
    if (inner instanceof z.ZodObject) {
      newShape[key] = withMetadata(deepPartial(inner).nullish(), value, inner)
    } else if (inner instanceof z.ZodArray && inner.element instanceof z.ZodObject) {
      newShape[key] = withMetadata(z.array(deepPartial(inner.element)).nullish(), value, inner)
    } else {
      newShape[key] = withMetadata(ignoreChecks(inner).nullish(), value, inner)
    }
  }

  // Carry forward object-level .check() refinements (cross-field rules). Field-level
  // constraints are intentionally dropped above so incomplete saves validate; cross-field
  // rules guard on presence and stay meaningful on partial data.
  const objectChecks = (schema.def.checks ?? []) as Parameters<z.ZodObject['check']>[number][]
  const partial = objectChecks.length > 0 ? z.object(newShape).check(...objectChecks) : z.object(newShape)

  return withMetadata(partial, schema)
}
